import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Copy, Check, ChevronDown, ExternalLink, Terminal, BookOpen } from "lucide-react";
import { trackEvent } from "../analytics";

// ── helpers ────────────────────────────────────────────────────────────────
function toSafeSlug(value) {
  const raw = (value || "").toString().trim().toLowerCase();
  return (
    raw.replace(/[^a-z0-9-_]+/g, "-").replace(/-+/g, "-").replace(/^[-_]+|[-_]+$/g, "") ||
    "my-api"
  );
}

function detectOs() {
  if (typeof navigator === "undefined") return "linux";
  const p = (navigator.userAgentData?.platform || navigator.platform || "").toLowerCase();
  if (p.includes("mac")) return "mac";
  if (p.includes("win")) return "windows";
  return "linux";
}

// ── CopyButton ─────────────────────────────────────────────────────────────
function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch {}
  };
  return (
    <button type="button" onClick={copy} title="Copy"
      className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
    >
      <AnimatePresence mode="wait" initial={false}>
        {copied
          ? <motion.span key="c" initial={{ scale: 0.6 }} animate={{ scale: 1 }} exit={{ scale: 0.6 }} className="flex"><Check className="w-3.5 h-3.5 text-emerald-500" /></motion.span>
          : <motion.span key="u" initial={{ scale: 0.6 }} animate={{ scale: 1 }} exit={{ scale: 0.6 }} className="flex"><Copy className="w-3.5 h-3.5" /></motion.span>
        }
      </AnimatePresence>
    </button>
  );
}

// ── CodeBlock — per-line copy buttons ─────────────────────────────────────
function CodeLine({ line }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(line); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch {}
  };
  return (
    <div className="group flex items-center justify-between gap-3 py-1">
      <code className="text-xs font-mono text-zinc-800 dark:text-zinc-200 break-all leading-relaxed flex-1">{line}</code>
      <button type="button" onClick={copy} title="Copy line"
        className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded text-zinc-400 dark:text-zinc-600 hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors opacity-0 group-hover:opacity-100"
      >
        <AnimatePresence mode="wait" initial={false}>
          {copied
            ? <motion.span key="c" initial={{ scale: 0.6 }} animate={{ scale: 1 }} exit={{ scale: 0.6 }} className="flex"><Check className="w-3 h-3 text-emerald-400" /></motion.span>
            : <motion.span key="u" initial={{ scale: 0.6 }} animate={{ scale: 1 }} exit={{ scale: 0.6 }} className="flex"><Copy className="w-3 h-3" /></motion.span>
          }
        </AnimatePresence>
      </button>
    </div>
  );
}

function CodeBlock({ code, label }) {
  const lines = code.split("\n");
  const isMultiLine = lines.length > 1;
  return (
    <div className="rounded-xl bg-muted/50 dark:bg-zinc-900 border border-border dark:border-zinc-800 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 border-b border-border dark:border-zinc-800">
        <span className="text-[11px] font-mono text-zinc-500 uppercase tracking-wider truncate">
          {label || "code"}
        </span>
        {/* Full-block copy always in header */}
        <CopyButton text={code} />
      </div>
      <div className="px-4 py-3">
        {isMultiLine ? (
          <div className="space-y-0.5">
            {lines.map((line, i) => line ? <CodeLine key={i} line={line} /> : <div key={i} className="h-2" />)}
          </div>
        ) : (
          <pre className="text-xs font-mono text-zinc-800 dark:text-zinc-200 overflow-x-auto whitespace-pre-wrap break-all leading-relaxed m-0">{code}</pre>
        )}
      </div>
    </div>
  );
}

// ── URL pill ───────────────────────────────────────────────────────────────
function UrlPill({ url }) {
  return (
    <div className="flex items-center gap-2 bg-muted/50 dark:bg-muted/30 border border-border rounded-xl px-4 py-3">
      <code className="flex-1 text-xs font-mono text-foreground break-all leading-relaxed">{url}</code>
      <CopyButton text={url} />
      <a href={url} target="_blank" rel="noreferrer" title="Open"
        className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
      >
        <ExternalLink className="w-3.5 h-3.5" />
      </a>
    </div>
  );
}

// ── Step ───────────────────────────────────────────────────────────────────
function Step({ n, children }) {
  return (
    <div className="flex items-start gap-3 text-sm text-foreground">
      <span className="mt-0.5 w-6 h-6 rounded-full bg-primary flex items-center justify-center flex-shrink-0 text-white text-[11px] font-bold">{n}</span>
      <span className="leading-relaxed pt-0.5">{children}</span>
    </div>
  );
}

// ── Tabs config ────────────────────────────────────────────────────────────
const TABS = [
  { id: "claude_web",     label: "claude.ai"      },
  { id: "claude_desktop", label: "Claude Desktop" },
  { id: "chatgpt",        label: "ChatGPT"        },
  { id: "cursor",         label: "Cursor"         },
  { id: "other",          label: "Other tools"    },
];

// ── Main ───────────────────────────────────────────────────────────────────
export default function ConnectPage({ specTitle, baseUrl, serverPath = "server.py", mcpUrl, sseUrl }) {
  const [activeTab, setActiveTab] = useState("claude_web");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const os   = useMemo(() => detectOs(), []);
  const slug = toSafeSlug(specTitle || "my-api");

  const displayMcpUrl = mcpUrl || "";
  const displaySseUrl = sseUrl || (mcpUrl ? mcpUrl.replace(/\/?$/, "/sse") : "");

  const claudeDesktopJson = JSON.stringify({ mcpServers: { [slug]: { url: displaySseUrl } } }, null, 2);
  const cursorJson        = JSON.stringify({ mcpServers: { [slug]: { url: displayMcpUrl } } }, null, 2);

  const otherTools = [
    { name: "Cline (VS Code)", config: JSON.stringify({ mcpServers: { [slug]: { url: displayMcpUrl } } }, null, 2) },
    { name: "Windsurf",        config: JSON.stringify({ mcpServers: { [slug]: { url: displayMcpUrl } } }, null, 2) },
    { name: "Continue",        config: JSON.stringify({ mcpServers: [{ name: slug, url: displayMcpUrl }] }, null, 2) },
  ];

  const [zipB64] = useState(() => {
    try { return sessionStorage.getItem("latest_mcp_zip_b64"); } catch { return null; }
  });

  function downloadZip() {
    if (!zipB64) return;
    trackEvent("Connect", "zip_downloaded", specTitle);
    const binary = atob(zipB64);
    const bytes  = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const blob = new Blob([bytes], { type: "application/zip" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url; a.download = `${slug}-mcp-server.zip`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  }

  const claudeConfigPath =
    os === "mac"     ? "~/Library/Application Support/Claude/claude_desktop_config.json" :
    os === "windows" ? "%APPDATA%\\Claude\\claude_desktop_config.json"                    :
                       "~/.config/Claude/claude_desktop_config.json";
  const cursorConfigPath =
    os === "windows" ? "%USERPROFILE%\\.cursor\\mcp.json" : "~/.cursor/mcp.json";

  return (
    <div className="max-w-2xl mx-auto px-4 pb-16">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
        className="mb-6 text-center"
      >
        <h2 className="text-xl font-bold text-foreground mb-1">Connect to your AI tool</h2>
        <p className="text-sm text-muted-foreground">Your MCP server is live in the cloud. Pick your tool below.</p>
      </motion.div>

      {/* Tabbed tool setup card */}
      <motion.div
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }}
        className="glass-strong rounded-2xl shadow-premium overflow-hidden"
      >
        {/* Tab bar */}
        <div className="flex overflow-x-auto border-b border-border/60 px-2 pt-1 gap-0.5" style={{ scrollbarWidth: "none" }}>
          {TABS.map((tab) => (
            <button key={tab.id} type="button"
              onClick={() => { setActiveTab(tab.id); trackEvent("Connect", "tool_selected", tab.id); }}
              className={`relative flex-shrink-0 px-4 py-2.5 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? "text-primary border-b-2 border-primary -mb-px"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-6">
          <AnimatePresence mode="wait">

            {activeTab === "claude_web" && (
              <motion.div key="cw" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.18 }} className="space-y-4">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Claude.ai — Web</p>
                <div className="space-y-3">
                  <Step n={1}>Go to <a href="https://claude.ai" target="_blank" rel="noreferrer" className="text-primary hover:underline">claude.ai</a> → <strong>Settings</strong> → <strong>Integrations</strong></Step>
                  <Step n={2}>Click <strong>Add integration</strong></Step>
                  <Step n={3}>Paste your MCP URL and click <strong>Connect</strong></Step>
                </div>
                {displayMcpUrl && <div className="pt-1"><p className="text-xs text-muted-foreground mb-2">Your URL:</p><UrlPill url={displayMcpUrl} /></div>}
              </motion.div>
            )}

            {activeTab === "claude_desktop" && (
              <motion.div key="cd" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.18 }} className="space-y-4">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Claude Desktop — SSE</p>
                <div className="space-y-3">
                  <Step n={1}>Open your <code className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono">claude_desktop_config.json</code> file</Step>
                  <Step n={2}>Add the snippet below under <code className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono">mcpServers</code></Step>
                  <Step n={3}>Save the file, then fully quit and relaunch Claude Desktop</Step>
                </div>
                <CodeBlock code={claudeDesktopJson} label="claude_desktop_config.json" />
                <div className="bg-muted/40 rounded-xl px-4 py-3 flex items-start gap-2 text-xs text-muted-foreground">
                  <Terminal className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-primary/60" />
                  <span className="break-all">Config file: <code className="font-mono text-foreground">{claudeConfigPath}</code></span>
                  <CopyButton text={claudeConfigPath} />
                </div>
              </motion.div>
            )}

            {activeTab === "chatgpt" && (
              <motion.div key="cgpt" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.18 }} className="space-y-4">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">ChatGPT Web / Desktop</p>
                <div className="space-y-3">
                  <Step n={1}>Go to <a href="https://chatgpt.com" target="_blank" rel="noreferrer" className="text-primary hover:underline">chatgpt.com</a> → <strong>Settings</strong> → <strong>Beta Features</strong> → <strong>Connectors</strong></Step>
                  <Step n={2}>Click <strong>Add custom MCP</strong></Step>
                  <Step n={3}>Paste your MCP URL and save</Step>
                </div>
                {displayMcpUrl && <div className="pt-1"><p className="text-xs text-muted-foreground mb-2">Your URL:</p><UrlPill url={displayMcpUrl} /></div>}
              </motion.div>
            )}

            {activeTab === "cursor" && (
              <motion.div key="cur" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.18 }} className="space-y-4">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Cursor</p>
                <div className="space-y-3">
                  <Step n={1}>Open the <a href="https://cursor.com" target="_blank" rel="noreferrer" className="text-primary hover:underline">Cursor</a> desktop app → <strong>Settings</strong> → <strong>Cursor Settings</strong> → <strong>MCP</strong></Step>
                  <Step n={2}>Open <code className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono">{cursorConfigPath}</code> (create if it doesn't exist) and add the snippet below under <code className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono">mcpServers</code></Step>
                  <Step n={3}>Fully quit and relaunch Cursor</Step>
                </div>
                <CodeBlock code={cursorJson} label={cursorConfigPath} />
              </motion.div>
            )}

            {activeTab === "other" && (
              <motion.div key="oth" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.18 }} className="space-y-5">
                <p className="text-sm text-muted-foreground">All MCP-compatible tools use the same URL. Add the snippet below to your tool's config file.</p>
                {otherTools.map((t) => (
                  <div key={t.name}>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-2">{t.name}</p>
                    <CodeBlock code={t.config} />
                  </div>
                ))}
                <div className="bg-muted/40 rounded-xl px-4 py-3 flex items-start gap-2 text-xs text-muted-foreground">
                  <BookOpen className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-primary/60" />
                  <span>Test it: ask the AI <em>"What tools do you have available?"</em> — if it lists your API tools, you're connected ✅</span>
                </div>
              </motion.div>
            )}

          </AnimatePresence>
        </div>
      </motion.div>

      {/* Advanced / Self-hosted (collapsed) */}
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4, delay: 0.2 }}
        className="mt-4"
      >
        <button type="button"
          onClick={() => setShowAdvanced((v) => !v)}
          className="w-full flex items-center justify-between px-5 py-3.5 rounded-xl border border-border bg-background hover:bg-muted/40 text-sm text-muted-foreground font-medium transition-colors"
        >
          <span>Advanced — Local / Self-hosted setup</span>
          <ChevronDown className={`w-4 h-4 transition-transform ${showAdvanced ? "rotate-180" : ""}`} />
        </button>

        <AnimatePresence>
          {showAdvanced && (
            <motion.div
              initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }}
              className="overflow-hidden"
            >
              <div className="glass-strong rounded-2xl shadow-premium p-6 mt-2 space-y-5">
                <div>
                  <p className="text-sm font-semibold text-foreground mb-1">Run the server locally</p>
                  <p className="text-xs text-muted-foreground">A ZIP with the full server source was downloaded automatically. Use it to run or self-host the MCP server.</p>
                </div>
                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-widest">Quick setup</p>
                  <CodeBlock code={`cd ~/Downloads\ncd ${slug}-mcp-server\npython setup.py`} label="Terminal" />
                  <p className="text-xs text-muted-foreground">Auto-installs dependencies and connects to Claude Desktop, Cursor, Cline, Windsurf, Continue.</p>
                </div>
                {zipB64 && (
                  <button type="button" onClick={downloadZip}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-border bg-background hover:bg-accent text-sm font-semibold text-foreground transition-all"
                  >
                    Re-download ZIP
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      <p className="text-xs text-muted-foreground text-center mt-6">
        Cloud URL stays live for 7 days · No auth required · Re-generate anytime
      </p>
    </div>
  );
}
